import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, PartyPopper, ArrowRight, LogOut, ShoppingBag, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { goToEtsy } from "@/lib/site";
import { Button } from "@/components/ui/button";

export default function Planners() {
  const { user, hasWedding, hasAccess, logout } = useAuth();
  const navigate = useNavigate();
  const ownsWedding = hasAccess || !!user?.products?.wedding_planner;
  const ownsBach = !!user?.products?.bachelorette_planner;
  const doLogout = async () => { navigate("/", { replace: true }); await logout(); };

  return (
    <div className="min-h-screen bg-[#FAF8F5] grain relative flex flex-col">
      <header className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-6">
        <div className="flex items-center gap-2.5"><div className="h-9 w-9 rounded-full bg-[#2C352D] flex items-center justify-center"><Heart className="h-4 w-4 text-white" fill="currentColor" /></div><span className="text-xl font-serif font-medium tracking-tight text-[#2A181C]">PlanMyWedding</span></div>
        <button onClick={doLogout} className="text-sm text-[#7A686C] inline-flex items-center gap-1.5 hover:text-[#2A181C]" data-testid="planners-logout"><LogOut className="h-4 w-4" /> Log out</button>
      </header>

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.28em] text-[#6B1D2F] font-semibold mb-3">My Planners</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2A181C]">Which planner today?</h1>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl w-full">
          {/* Wedding */}
          <div className="bg-[#F4F1EB] border border-[#E5E0D8] rounded-3xl p-8 flex flex-col justify-between hover:shadow-xl transition-all" data-testid="planner-card-wedding">
            <div>
              <div className="h-11 w-11 rounded-full bg-[#2C352D] flex items-center justify-center mb-4"><Heart className="h-5 w-5 text-white" fill="currentColor" /></div>
              <h2 className="font-serif text-2xl text-[#2C352D]">Wedding Planner</h2>
              <p className="text-sm text-[#666] mt-2">Budget, guests, seating, vendors and your whole wedding day.</p>
            </div>
            {ownsWedding ? (
              <Button onClick={() => navigate(hasWedding ? "/app" : "/onboarding")} className="w-full rounded-full mt-6 bg-[#2C352D] hover:bg-[#232a24] text-white" data-testid="open-wedding">Open Planner <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
            ) : (
              <div className="mt-6 space-y-2">
                <Button onClick={() => navigate("/demo/wedding")} className="w-full rounded-full bg-[#2C352D] hover:bg-[#232a24] text-white" data-testid="demo-wedding">Explore Demo <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
                <Button onClick={goToEtsy} variant="outline" className="w-full rounded-full border-[#2C352D]/30" data-testid="get-wedding"><ShoppingBag className="h-4 w-4 mr-2" /> Get Full Access</Button>
              </div>
            )}
          </div>

          {/* Bachelorette */}
          <div className="bg-gradient-to-br from-[#FAF8F5] via-[#F9ECE8] to-[#F5EFEA] border border-[#ECCDC5] rounded-3xl p-8 flex flex-col justify-between hover:shadow-xl hover:border-[#6B1D2F]/30 transition-all relative" data-testid="planner-card-bachelorette">
            <div>
              <div className="h-11 w-11 rounded-full bg-[#6B1D2F] flex items-center justify-center mb-4"><PartyPopper className="h-5 w-5 text-white" /></div>
              <h2 className="font-serif text-2xl text-[#2A181C]">Bachelorette Planner</h2>
              <p className="text-sm text-[#7A686C] mt-2">Guests, group payments, itinerary, activities, outfits and more.</p>
            </div>
            {ownsBach ? (
              <Button onClick={() => navigate("/bach")} className="w-full rounded-full mt-6 bg-[#6B1D2F] hover:bg-[#541423] text-white" data-testid="open-bachelorette">Open Planner <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
            ) : (
              <div className="mt-6 space-y-2">
                <Button onClick={() => navigate("/demo/bachelorette")} className="w-full rounded-full bg-[#6B1D2F] hover:bg-[#541423] text-white" data-testid="demo-bachelorette">Explore Demo <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
                <Button onClick={() => goToEtsy("bachelorette")} variant="outline" className="w-full rounded-full border-[#6B1D2F]/30 text-[#6B1D2F]" data-testid="get-bachelorette"><ShoppingBag className="h-4 w-4 mr-2" /> Get Full Access</Button>
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-[#7A686C] text-center mt-8 inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-[#6B1D2F]" /> Each planner is a separate product with its own private data.</p>
      </div>
    </div>
  );
}
